import type { ChartDescriptor, ChartType } from '../types'

export const CHART_REGISTRY: Record<ChartType, ChartDescriptor> = {
  bar: {
    type:                 'bar',
    label:                'Bar Chart',
    description:          'Compare values across categories with vertical or horizontal bars',
    compatibleMetrics:    ['queue', 'session', 'performance', 'team'],
    compatibleDimensions: ['agent', 'team', 'status', 'outcome', 'reason', 'priority', 'city', 'country'],
    requiresDimension:    true,
    maxMetrics:           3,
    minDataPoints:        2,
  },
  line: {
    type:                 'line',
    label:                'Line Chart',
    description:          'Show trends over time with continuous lines',
    compatibleMetrics:    ['queue', 'performance', 'session'],
    compatibleDimensions: ['date'],
    requiresDimension:    true,
    maxMetrics:           5,
    minDataPoints:        3,
  },
  area: {
    type:                 'area',
    label:                'Area Chart',
    description:          'Like a line chart but with filled areas — emphasizes volume over time',
    compatibleMetrics:    ['queue', 'performance'],
    compatibleDimensions: ['date'],
    requiresDimension:    true,
    maxMetrics:           3,
    minDataPoints:        3,
  },
  pie: {
    type:                 'pie',
    label:                'Pie Chart',
    description:          'Show proportional breakdown of a single metric across categories',
    compatibleMetrics:    ['queue', 'session'],
    compatibleDimensions: ['status', 'outcome', 'reason', 'priority', 'workflow'],
    requiresDimension:    true,
    maxMetrics:           1,
    minDataPoints:        2,
  },
  donut: {
    type:                 'donut',
    label:                'Donut Chart',
    description:          'Pie chart with a hollow center — good for a KPI in the middle',
    compatibleMetrics:    ['queue', 'session', 'performance'],
    compatibleDimensions: ['status', 'outcome', 'reason', 'priority', 'workflow'],
    requiresDimension:    true,
    maxMetrics:           1,
    minDataPoints:        2,
  },
  heatmap: {
    type:                 'heatmap',
    label:                'Heatmap',
    description:          'Show intensity across two dimensions (e.g., hour × day)',
    compatibleMetrics:    ['queue', 'session', 'performance'],
    compatibleDimensions: ['date', 'agent', 'team'],
    requiresDimension:    true,
    maxMetrics:           1,
    minDataPoints:        4,
  },
  funnel: {
    type:                 'funnel',
    label:                'Funnel Chart',
    description:          'Visualise sequential stages with drop-off between each step',
    compatibleMetrics:    ['queue', 'session'],
    compatibleDimensions: ['status', 'outcome'],
    requiresDimension:    true,
    maxMetrics:           1,
    minDataPoints:        2,
  },
  table: {
    type:                 'table',
    label:                'Table',
    description:          'Show raw rows and columns — supports sorting, filtering, pagination',
    compatibleMetrics:    ['queue', 'session', 'performance', 'team', 'escalation'],
    compatibleDimensions: ['date', 'agent', 'team', 'status', 'outcome', 'reason', 'priority', 'channel', 'city', 'country'],
    requiresDimension:    false,
    maxMetrics:           20,
    minDataPoints:        1,
  },
  leaderboard: {
    type:                 'leaderboard',
    label:                'Leaderboard',
    description:          'Rank agents or teams by a single metric',
    compatibleMetrics:    ['session', 'performance'],
    compatibleDimensions: ['agent', 'team'],
    requiresDimension:    true,
    maxMetrics:           1,
    minDataPoints:        2,
  },
  timeline: {
    type:                 'timeline',
    label:                'Timeline',
    description:          'Show events and durations on a chronological axis',
    compatibleMetrics:    ['session'],
    compatibleDimensions: ['date', 'agent'],
    requiresDimension:    true,
    maxMetrics:           2,
    minDataPoints:        1,
  },
}

export const CHART_LIST: ChartDescriptor[] = Object.values(CHART_REGISTRY)

export function getCompatibleCharts(
  metricCategories: ChartDescriptor['compatibleMetrics'][number][],
  hasDimension:     boolean,
  rowCount:         number,
): ChartDescriptor[] {
  return CHART_LIST.filter((c) => {
    if (c.minDataPoints > rowCount && rowCount > 0) return false
    if (c.requiresDimension && !hasDimension)         return false
    return metricCategories.some((cat) => c.compatibleMetrics.includes(cat))
  })
}
